import React, { useState, useEffect, useRef } from 'react';
import AnalyticsDashboard from '../components/recruiter/AnalyticsDashboard';
import CommunicationPanel from '../components/recruiter/CommunicationPanel';
import { 
    Briefcase, Users, Calendar, Mail, TrendingUp, Clock, Search, Filter,
    Plus, ChevronDown, ChevronRight, Star, MapPin, Phone, FileText,
    MessageCircle, Video, CheckCircle, XCircle, AlertCircle, Timer,
    Eye, Edit, Send, Bell, BarChart3, Target, Award, Zap, LayoutDashboard
} from 'lucide-react';

const RecruiterWorkspace = () => {
    const [selectedView, setSelectedView] = useState('dashboard');
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [selectedRequisition, setSelectedRequisition] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [draggedCandidate, setDraggedCandidate] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [showNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
    const [showComposeModal, setShowComposeModal] = useState(false);
    const [filters, setFilters] = useState({
        role: '',
        source: '',
        location: '',
        recruiter: '',
        department: '',
        status: ''
    });

    // Mock data for recruiter workspace
    const [dashboardData, setDashboardData] = useState({
        activeRequisitions: 12,
        candidatesInProcess: 89,
        interviewsToday: 5,
        offersPending: 3,
        pipelineStats: {
            applied: 145,
            screening: 67,
            interview: 23,
            offer: 8,
            hired: 12,
            rejected: 89
        },
        recentActivity: [
            { id: 1, type: 'interview', candidate: 'Sarah Johnson', job: 'Senior Developer', action: 'Interview completed', time: '30 min ago' },
            { id: 2, type: 'application', candidate: 'Mike Chen', job: 'Product Manager', action: 'New application', time: '1 hour ago' },
            { id: 3, type: 'offer', candidate: 'Emily Rodriguez', job: 'UX Designer', action: 'Offer accepted', time: '2 hours ago' }
        ],
        tasks: [
            { id: 1, task: 'Review Sarah Johnson interview feedback', priority: 'high', due: 'Today 3:00 PM' },
            { id: 2, task: 'Schedule final round for Mike Chen', priority: 'medium', due: 'Tomorrow' },
            { id: 3, task: 'Follow up on Emily offer', priority: 'low', due: 'This week' }
        ]
    });

    const [requisitions, setRequisitions] = useState([
        {
            id: 'req-001',
            title: 'Senior Full-Stack Developer',
            hiringManager: 'John Smith',
            department: 'Engineering',
            openings: 2,
            activeCandidates: 15,
            status: 'Active',
            agingDays: 12,
            location: 'San Francisco, CA',
            priority: 'High',
            description: 'Looking for an experienced full-stack developer...',
            requirements: ['5+ years React', '3+ years Node.js', 'AWS experience']
        },
        {
            id: 'req-002',
            title: 'Product Manager',
            hiringManager: 'Lisa Chen',
            department: 'Product',
            openings: 1,
            activeCandidates: 8,
            status: 'Active',
            agingDays: 25,
            location: 'New York, NY',
            priority: 'Medium',
            description: 'Seeking a strategic product manager...',
            requirements: ['3+ years PM experience', 'B2B SaaS background', 'Analytics skills']
        }
    ]);

    const [candidates, setCandidates] = useState([
        {
            id: 'cand-001',
            name: 'Sarah Johnson',
            role: 'Senior Developer',
            source: 'LinkedIn',
            stage: 'interview',
            rating: 4.5,
            recruiter: 'You',
            email: 'sarah.j@email.com',
            phone: '+1 (555) 123-4567',
            location: 'San Francisco, CA',
            experience: '7 years',
            skills: ['React', 'Node.js', 'Python', 'AWS'],
            notes: 'Strong technical background, great cultural fit',
            interviewDate: '2024-01-15T14:00:00Z',
            requisitionId: 'req-001'
        },
        {
            id: 'cand-002',
            name: 'Mike Chen',
            role: 'Product Manager',
            source: 'Referral',
            stage: 'screening',
            rating: 4.2,
            recruiter: 'You',
            email: 'mike.chen@email.com',
            phone: '+1 (555) 234-5678',
            location: 'New York, NY',
            experience: '5 years',
            skills: ['Product Strategy', 'Analytics', 'User Research'],
            notes: 'Great product sense, needs technical depth assessment',
            requisitionId: 'req-002'
        },
        {
            id: 'cand-003',
            name: 'Emily Rodriguez',
            role: 'UX Designer',
            source: 'Indeed',
            stage: 'offer',
            rating: 4.8,
            recruiter: 'You',
            email: 'emily.r@email.com',
            phone: '+1 (555) 345-6789',
            location: 'Austin, TX',
            experience: '4 years',
            skills: ['Figma', 'User Research', 'Prototyping'],
            notes: 'Outstanding portfolio, excellent communication skills',
            requisitionId: 'req-001'
        }
    ]);

    const pipelineStages = [
        { id: 'applied', name: 'Applied', count: 0, color: 'bg-blue-50 border-blue-200' },
        { id: 'screening', name: 'Screening', count: 0, color: 'bg-yellow-50 border-yellow-200' },
        { id: 'interview', name: 'Interview', count: 0, color: 'bg-purple-50 border-purple-200' },
        { id: 'offer', name: 'Offer', count: 0, color: 'bg-green-50 border-green-200' },
        { id: 'hired', name: 'Hired', count: 0, color: 'bg-emerald-50 border-emerald-200' },
        { id: 'rejected', name: 'Rejected', count: 0, color: 'bg-red-50 border-red-200' }
    ];

    // Update pipeline stage counts
    useEffect(() => {
        const stageCounts = candidates.reduce((acc, candidate) => {
            acc[candidate.stage] = (acc[candidate.stage] || 0) + 1;
            return acc;
        }, {});

        pipelineStages.forEach(stage => {
            stage.count = stageCounts[stage.id] || 0;
        });
    }, [candidates]);

    // Toast notification system
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    // Action handlers with feedback
    const handleSendMessage = async (candidateId, message) => {
        setLoading(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToast('Message sent successfully!');
            setShowComposeModal(false);
        } catch (error) {
            showToast('Failed to send message. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleScheduleInterview = async (candidateId) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            showToast('Interview scheduled successfully!');
            // Update dashboard data
            setDashboardData(prev => ({
                ...prev,
                interviewsToday: prev.interviewsToday + 1
            }));
        } catch (error) {
            showToast('Failed to schedule interview. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleAddNote = async (candidateId, note) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            showToast('Note added successfully!');
            // Update candidate notes
            setCandidates(prev => prev.map(candidate => 
                candidate.id === candidateId 
                    ? { ...candidate, notes: `${candidate.notes}\n\n${note}` }
                    : candidate
            ));
        } catch (error) {
            showToast('Failed to add note. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleCreateRequisition = async (requisitionData) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1200));
            const newRequisition = {
                id: `req-${Date.now()}`,
                ...requisitionData,
                activeCandidates: 0,
                status: 'Active',
                agingDays: 0
            };
            setRequisitions(prev => [...prev, newRequisition]);
            setDashboardData(prev => ({
                ...prev,
                activeRequisitions: prev.activeRequisitions + 1
            }));
            showToast('Job requisition created successfully!');
            setShowNewRequisitionModal(false);
        } catch (error) {
            showToast('Failed to create requisition. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleMoveCandidate = async (candidateId, newStage) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            setCandidates(prev => prev.map(candidate => 
                candidate.id === candidateId 
                    ? { ...candidate, stage: newStage }
                    : candidate
            ));
            
            const candidate = candidates.find(c => c.id === candidateId);
            showToast(`${candidate?.name} moved to ${newStage} stage`);
            
            // Update dashboard activity
            const newActivity = {
                id: Date.now(),
                type: 'stage_change',
                candidate: candidate?.name,
                job: candidate?.role,
                action: `Moved to ${newStage}`,
                time: 'Just now'
            };
            setDashboardData(prev => ({
                ...prev,
                recentActivity: [newActivity, ...prev.recentActivity.slice(0, 2)]
            }));
        } catch (error) {
            showToast('Failed to move candidate. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleBulkAction = async (selectedCandidates, action) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            showToast(`${action} applied to ${selectedCandidates.length} candidates`);
        } catch (error) {
            showToast('Bulk action failed. Please try again.', 'error');
        }
        setLoading(false);
    };

    const handleExportReport = async (reportType) => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            showToast(`${reportType} report exported successfully!`);
            // In real implementation, this would trigger file download
        } catch (error) {
            showToast('Export failed. Please try again.', 'error');
        }
        setLoading(false);
    };

    // Enhanced Drag and Drop handlers with feedback
    const handleDragStart = (e, candidate) => {
        setDraggedCandidate(candidate);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedCandidate(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        e.currentTarget.style.backgroundColor = '#f3f4f6';
    };

    const handleDragLeave = (e) => {
        e.currentTarget.style.backgroundColor = '';
    };

    const handleDrop = async (e, newStage) => {
        e.preventDefault();
        e.currentTarget.style.backgroundColor = '';
        
        if (draggedCandidate && draggedCandidate.stage !== newStage) {
            await handleMoveCandidate(draggedCandidate.id, newStage);
        }
        setDraggedCandidate(null);
    };

    // Dashboard Component
    const Dashboard = () => (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Briefcase className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-600">Active Requisitions</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.activeRequisitions}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Users className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-600">Candidates in Process</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.candidatesInProcess}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Calendar className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-600">Interviews Today</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.interviewsToday}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <Award className="h-6 w-6 text-yellow-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-600">Offers Pending</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.offersPending}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pipeline Summary & Tasks */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pipeline Summary */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold mb-4">Submissions Overview</h3>
                    <div className="space-y-3">
                        {pipelineStages.map((stage) => (
                            <div key={stage.id} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                        <div 
                                            className={`h-2 rounded-full ${stage.id === 'hired' ? 'bg-green-600' : stage.id === 'rejected' ? 'bg-red-600' : 'bg-blue-600'}`}
                                            style={{ width: `${Math.min((stage.count / Math.max(dashboardData.candidatesInProcess, 1)) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900 w-8 text-right">{stage.count}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button 
                        onClick={() => {
                            setSelectedView('pipeline');
                            showToast('Switching to submissions view');
                        }}
                        className="mt-4 w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        View All Submissions
                    </button>
                </div>

                {/* Tasks & Reminders */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Tasks & Reminders</h3>
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">View All</button>
                    </div>
                    <div className="space-y-3">
                        {dashboardData.tasks.map((task) => (
                            <div key={task.id} className="flex items-start space-x-3">
                                <div className={`w-2 h-2 rounded-full mt-2 ${
                                    task.priority === 'high' ? 'bg-red-500' : 
                                    task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                }`}></div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{task.task}</p>
                                    <p className="text-xs text-gray-500">{task.due}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                <div className="space-y-4">
                    {dashboardData.recentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-center space-x-4">
                            <div className={`p-2 rounded-lg ${
                                activity.type === 'interview' ? 'bg-purple-100' :
                                activity.type === 'application' ? 'bg-blue-100' : 'bg-green-100'
                            }`}>
                                {activity.type === 'interview' && <Calendar className="h-4 w-4 text-purple-600" />}
                                {activity.type === 'application' && <Users className="h-4 w-4 text-blue-600" />}
                                {activity.type === 'offer' && <Award className="h-4 w-4 text-green-600" />}
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                    <span className="font-semibold">{activity.candidate}</span> - {activity.job}
                                </p>
                                <p className="text-xs text-gray-500">{activity.action} • {activity.time}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // Candidate Pipeline Component
    const CandidatePipeline = () => (
        <div className="space-y-6">
            {/* Pipeline Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Candidate Pipeline</h3>
                    <div className="flex space-x-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <button 
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            <Filter className="h-4 w-4 mr-2" />
                            Filters
                        </button>
                    </div>
                </div>

                {/* Filters */}
                {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
                        <select 
                            value={filters.role}
                            onChange={(e) => setFilters({...filters, role: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Roles</option>
                            <option value="Senior Developer">Senior Developer</option>
                            <option value="Product Manager">Product Manager</option>
                            <option value="UX Designer">UX Designer</option>
                        </select>
                        <select 
                            value={filters.source}
                            onChange={(e) => setFilters({...filters, source: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Sources</option>
                            <option value="LinkedIn">LinkedIn</option>
                            <option value="Indeed">Indeed</option>
                            <option value="Referral">Referral</option>
                        </select>
                        <select 
                            value={filters.location}
                            onChange={(e) => setFilters({...filters, location: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Locations</option>
                            <option value="San Francisco">San Francisco</option>
                            <option value="New York">New York</option>
                            <option value="Austin">Austin</option>
                        </select>
                        <select 
                            value={filters.recruiter}
                            onChange={(e) => setFilters({...filters, recruiter: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Recruiters</option>
                            <option value="You">You</option>
                            <option value="Team Member 1">Team Member 1</option>
                            <option value="Team Member 2">Team Member 2</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Kanban Pipeline */}
            <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
                <div className="flex space-x-4 p-6 min-w-max">
                    {pipelineStages.map((stage) => (
                        <div 
                            key={stage.id} 
                            className={`flex-shrink-0 w-72 ${stage.color} border-2 border-dashed rounded-lg p-4 transition-colors`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, stage.id)}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="font-semibold text-gray-900">{stage.name}</h4>
                                <span className="bg-gray-100 text-gray-700 text-sm font-medium px-2 py-1 rounded-full">
                                    {candidates.filter(c => c.stage === stage.id).length}
                                </span>
                            </div>
                            
                            <div className="space-y-3">
                                {candidates
                                    .filter(candidate => candidate.stage === stage.id)
                                    .filter(candidate => {
                                        // Search filter
                                        if (searchTerm && !candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
                                            !candidate.role.toLowerCase().includes(searchTerm.toLowerCase())) {
                                            return false;
                                        }
                                        
                                        // Role filter
                                        if (filters.role && candidate.role !== filters.role) {
                                            return false;
                                        }
                                        
                                        // Source filter
                                        if (filters.source && candidate.source !== filters.source) {
                                            return false;
                                        }
                                        
                                        // Location filter
                                        if (filters.location && !candidate.location.includes(filters.location)) {
                                            return false;
                                        }
                                        
                                        // Recruiter filter
                                        if (filters.recruiter && candidate.recruiter !== filters.recruiter) {
                                            return false;
                                        }
                                        
                                        return true;
                                    })
                                    .map((candidate) => (
                                        <CandidateCard 
                                            key={candidate.id} 
                                            candidate={candidate} 
                                            onDragStart={handleDragStart}
                                            onClick={() => setSelectedCandidate(candidate)}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // Enhanced Candidate Card Component with interactive actions
    const CandidateCard = ({ candidate, onDragStart, onClick }) => (
        <div 
            className="bg-white p-4 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            draggable
            onDragStart={(e) => onDragStart(e, candidate)}
            onDragEnd={handleDragEnd}
        >
            <div 
                className="cursor-pointer" 
                onClick={() => onClick(candidate)}
            >
                <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 truncate">{candidate.name}</h5>
                    <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                            <Star 
                                key={i} 
                                className={`h-3 w-3 ${i < Math.floor(candidate.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                            />
                        ))}
                    </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">{candidate.role}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{candidate.source}</span>
                    <span>{candidate.experience}</span>
                </div>
            </div>
            <div className="mt-2 flex space-x-2">
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCandidate(candidate);
                        showToast(`Viewing ${candidate.name}'s profile`);
                    }}
                    className="flex-1 py-1 px-2 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    disabled={loading}
                >
                    View
                </button>
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowComposeModal(true);
                        showToast(`Opening message composer for ${candidate.name}`);
                    }}
                    className="flex-1 py-1 px-2 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                    disabled={loading}
                >
                    Contact
                </button>
            </div>
        </div>
    );

    // Job Requisitions Component
    const JobRequisitions = () => (
        <div className="space-y-6">
            {/* Requisitions Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Job Requisitions</h3>
                    <button 
                        onClick={() => setShowNewRequisitionModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        New Requisition
                    </button>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option>All Departments</option>
                        <option>Engineering</option>
                        <option>Product</option>
                        <option>Design</option>
                    </select>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option>All Status</option>
                        <option>Active</option>
                        <option>On Hold</option>
                        <option>Closed</option>
                    </select>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option>All Locations</option>
                        <option>San Francisco</option>
                        <option>New York</option>
                        <option>Remote</option>
                    </select>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option>Sort by Age</option>
                        <option>Sort by Title</option>
                        <option>Sort by Candidates</option>
                    </select>
                </div>
            </div>

            {/* Requisitions List */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hiring Manager</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Openings</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Candidates</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aging Days</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {requisitions.map((req) => (
                                <tr key={req.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">{req.title}</div>
                                            <div className="text-sm text-gray-500">{req.department} • {req.location}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{req.hiringManager}</td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{req.openings}</td>
                                    <td className="px-6 py-4 text-sm text-gray-900">{req.activeCandidates}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            req.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {req.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        <span className={req.agingDays > 30 ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                                            {req.agingDays} days
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => {
                                                    setSelectedRequisition(req);
                                                    showToast(`Viewing details for ${req.title}`);
                                                }}
                                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                                title="View Details"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button 
                                                onClick={() => showToast(`Edit functionality for ${req.title} coming soon`, 'info')}
                                                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                                title="Edit Requisition"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setSelectedView('pipeline');
                                                    showToast(`Viewing candidates for ${req.title}`);
                                                }}
                                                className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                                                title="View Candidates"
                                            >
                                                <Users className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    // Candidate Profile Drawer
    const CandidateProfileDrawer = ({ candidate, onClose }) => {
        if (!candidate) return null;

        return (
            <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl z-50 overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold">Candidate Profile</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <XCircle className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Candidate Header */}
                    <div className="text-center mb-6">
                        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-xl font-semibold text-blue-600">
                                {candidate.name.split(' ').map(n => n[0]).join('')}
                            </span>
                        </div>
                        <h3 className="text-lg font-semibold">{candidate.name}</h3>
                        <p className="text-gray-600">{candidate.role}</p>
                        <div className="flex items-center justify-center mt-2">
                            {[...Array(5)].map((_, i) => (
                                <Star 
                                    key={i} 
                                    className={`h-4 w-4 ${i < Math.floor(candidate.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                />
                            ))}
                            <span className="ml-2 text-sm text-gray-600">{candidate.rating}</span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="flex space-x-8">
                            <button className="border-b-2 border-blue-500 text-blue-600 py-2 px-1 text-sm font-medium">
                                Overview
                            </button>
                            <button className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium">
                                Activity
                            </button>
                            <button className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium">
                                Interview
                            </button>
                        </nav>
                    </div>

                    {/* Overview Tab */}
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Mail className="h-4 w-4 mr-2" />
                                    {candidate.email}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <Phone className="h-4 w-4 mr-2" />
                                    {candidate.phone}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                    <MapPin className="h-4 w-4 mr-2" />
                                    {candidate.location}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Experience</h4>
                            <p className="text-sm text-gray-600">{candidate.experience} of experience</p>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Skills</h4>
                            <div className="flex flex-wrap gap-2">
                                {candidate.skills.map((skill, index) => (
                                    <span 
                                        key={index}
                                        className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                                    >
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Notes</h4>
                            <p className="text-sm text-gray-600">{candidate.notes}</p>
                        </div>

                        <div className="pt-4 border-t space-y-3">
                            <button 
                                onClick={() => {
                                    setShowComposeModal(true);
                                    showToast(`Opening message composer for ${candidate.name}`);
                                }}
                                disabled={loading}
                                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50"
                            >
                                <Mail className="h-4 w-4 mr-2" />
                                {loading ? 'Loading...' : 'Send Message'}
                            </button>
                            <button 
                                onClick={() => handleScheduleInterview(candidate.id)}
                                disabled={loading}
                                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center disabled:opacity-50"
                            >
                                <Calendar className="h-4 w-4 mr-2" />
                                {loading ? 'Scheduling...' : 'Schedule Interview'}
                            </button>
                            <button 
                                onClick={() => {
                                    const note = prompt('Enter note for ' + candidate.name + ':');
                                    if (note) {
                                        handleAddNote(candidate.id, note);
                                    }
                                }}
                                disabled={loading}
                                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center disabled:opacity-50"
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                {loading ? 'Adding...' : 'Add Note'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Main Navigation
    const workspaceViews = [
        { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
        { id: 'pipeline', name: 'Candidate Pipeline', icon: Users },
        { id: 'requisitions', name: 'Job Requisitions', icon: Briefcase },
        { id: 'communications', name: 'Communications', icon: MessageCircle },
        { id: 'analytics', name: 'Analytics', icon: BarChart3 }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <Zap className="h-8 w-8 text-blue-600 mr-3" />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Recruiter Workspace</h1>
                                <p className="text-sm text-gray-600">Your unified recruiting dashboard</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <button className="relative p-2 text-gray-400 hover:text-gray-600">
                                <Bell className="h-5 w-5" />
                                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">3</span>
                            </button>
                            <div className="text-sm">
                                <p className="font-medium text-gray-900">Sarah Smith</p>
                                <p className="text-gray-500">Senior Recruiter</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex space-x-8">
                        {workspaceViews.map((view) => {
                            const Icon = view.icon;
                            return (
                                <button
                                    key={view.id}
                                    onClick={() => setSelectedView(view.id)}
                                    className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 ${
                                        selectedView === view.id
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    <Icon className="h-4 w-4 mr-2" />
                                    {view.name}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {selectedView === 'dashboard' && <Dashboard />}
                {selectedView === 'pipeline' && <CandidatePipeline />}
                {selectedView === 'requisitions' && <JobRequisitions />}
                {selectedView === 'communications' && <CommunicationPanel />}
                {selectedView === 'analytics' && <AnalyticsDashboard />}
            </div>

            {/* Candidate Profile Drawer */}
            {selectedCandidate && (
                <CandidateProfileDrawer 
                    candidate={selectedCandidate} 
                    onClose={() => setSelectedCandidate(null)} 
                />
            )}

            {/* Requisition Detail Modal */}
            {selectedRequisition && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">{selectedRequisition.title}</h2>
                            <button 
                                onClick={() => setSelectedRequisition(null)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle className="h-6 w-6" />
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Department</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedRequisition.department}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Hiring Manager</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedRequisition.hiringManager}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Location</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedRequisition.location}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Openings</label>
                                    <p className="mt-1 text-sm text-gray-900">{selectedRequisition.openings}</p>
                                </div>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Description</label>
                                <p className="mt-1 text-sm text-gray-900">{selectedRequisition.description}</p>
                            </div>
                            
                            <div>
                                <label className="text-sm font-medium text-gray-500">Requirements</label>
                                <ul className="mt-1 list-disc list-inside space-y-1">
                                    {selectedRequisition.requirements.map((req, index) => (
                                        <li key={index} className="text-sm text-gray-900">{req}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mt-6 pt-6 border-t flex justify-end space-x-3">
                            <button 
                                onClick={() => setSelectedRequisition(null)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => setSelectedView('pipeline')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                View Pipeline
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay for drawer */}
            {selectedCandidate && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-30 z-40"
                    onClick={() => setSelectedCandidate(null)}
                />
            )}

            {/* Toast Notifications */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
                    toast.type === 'success' ? 'bg-green-500 text-white' :
                    toast.type === 'error' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                }`}>
                    <div className="flex items-center">
                        {toast.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
                        {toast.type === 'error' && <XCircle className="h-5 w-5 mr-2" />}
                        {toast.type === 'info' && <AlertCircle className="h-5 w-5 mr-2" />}
                        <span>{toast.message}</span>
                        <button 
                            onClick={() => setToast(null)}
                            className="ml-4 text-white hover:text-gray-200"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* New Requisition Modal */}
            {showNewRequisitionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">Create New Job Requisition</h2>
                            <button 
                                onClick={() => setShowNewRequisitionModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle className="h-6 w-6" />
                            </button>
                        </div>
                        
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            const requisitionData = {
                                title: formData.get('title'),
                                department: formData.get('department'),
                                hiringManager: formData.get('hiringManager'),
                                location: formData.get('location'),
                                openings: parseInt(formData.get('openings')),
                                priority: formData.get('priority'),
                                description: formData.get('description'),
                                requirements: formData.get('requirements').split('\n').filter(r => r.trim())
                            };
                            handleCreateRequisition(requisitionData);
                        }}>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                                    <input 
                                        type="text" 
                                        name="title"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g. Senior React Developer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                                    <select 
                                        name="department"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Department</option>
                                        <option value="Engineering">Engineering</option>
                                        <option value="Product">Product</option>
                                        <option value="Design">Design</option>
                                        <option value="Marketing">Marketing</option>
                                        <option value="Sales">Sales</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Hiring Manager</label>
                                    <input 
                                        type="text" 
                                        name="hiringManager"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Manager name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                    <input 
                                        type="text" 
                                        name="location"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g. San Francisco, CA"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Openings</label>
                                    <input 
                                        type="number" 
                                        name="openings"
                                        required
                                        min="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                    <select 
                                        name="priority"
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select Priority</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Job Description</label>
                                <textarea 
                                    name="description"
                                    required
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter job description..."
                                />
                            </div>
                            
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Requirements (one per line)</label>
                                <textarea 
                                    name="requirements"
                                    required
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="5+ years React experience&#10;3+ years Node.js&#10;AWS experience"
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button 
                                    type="button"
                                    onClick={() => setShowNewRequisitionModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create Requisition'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Compose Message Modal */}
            {showComposeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">Send Message</h2>
                            <button 
                                onClick={() => setShowComposeModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle className="h-6 w-6" />
                            </button>
                        </div>
                        
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            const message = formData.get('message');
                            handleSendMessage(selectedCandidate?.id, message);
                        }}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                                <input 
                                    type="text" 
                                    value={selectedCandidate?.email || ''}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                />
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                                <input 
                                    type="text" 
                                    name="subject"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter subject..."
                                />
                            </div>
                            
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                                <textarea 
                                    name="message"
                                    required
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Type your message..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button 
                                    type="button"
                                    onClick={() => setShowComposeModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    {loading ? 'Sending...' : 'Send Message'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecruiterWorkspace;